// src/components/common/Modal.jsx
import React from 'react';
import { LuX } from 'react-icons/lu';

const Modal = ({
  isOpen = false,
  onClose,
  title,
  children,
  size = 'md', // sm, md, lg, xl
  showCloseButton = true,
  closeOnOverlayClick = true,
  showFooter = false,
  footerContent = null,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  isConfirmDisabled = false,
  confirmButtonType = 'primary', // primary, danger, success
  maxHeight = '80vh',
  padding = 'p-6',
  showBackdrop = true,
  backdropClass = 'bg-black bg-opacity-50',
  animation = true,
  className = '',
}) => {
  if (!isOpen) return null;

  // Size classes
  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  // Confirm button color classes
  const confirmButtonClasses = {
    primary: 'bg-orange-600 hover:bg-orange-700 text-white',
    danger: 'bg-red-600 hover:bg-red-700 text-white',
    success: 'bg-orange-600 hover:bg-orange-700 text-white',
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && closeOnOverlayClick) {
      onClose?.();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      onClose?.();
    }
  };

  const handleCancel = () => {
    onCancel?.() || onClose?.();
  };

  const handleConfirm = () => {
    if (!isLoading && !isConfirmDisabled) {
      onConfirm?.();
    }
  };

  return (
    <div 
      className={`
        fixed inset-0 z-50 overflow-y-auto
        ${animation ? 'transition-opacity duration-300 ease-in-out' : ''}
        ${showBackdrop ? backdropClass : ''}
      `}
      onClick={handleOverlayClick}
    >
      {/* Modal Container */}
      <div className="flex min-h-screen items-center justify-center p-4">
        {/* Modal Content */}
        <div
          className={`
            relative bg-white rounded-xl shadow-xl w-full
            ${sizeClasses[size]}
            ${animation ? 'transition-all duration-300 ease-out transform scale-95' : ''}
            ${className}
          `}
          style={{
            animation: animation ? 'modalSlideIn 0.3s ease-out forwards' : 'none',
          }}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
            <h3 className="text-lg font-semibold text-gray-900">
              {title}
            </h3>
            
            {showCloseButton && (
              <button
                onClick={handleClose}
                disabled={isLoading}
                className={`
                  p-1 rounded-lg transition-colors
                  ${isLoading 
                    ? 'opacity-50 cursor-not-allowed' 
                    : 'hover:bg-gray-100 text-gray-500 hover:text-gray-700'
                  }
                `}
                aria-label="Close modal"
              >
                <LuX className="w-5 h-5" />
              </button>
            )}
          </div>

          {/* Body */}
          <div 
            className={`${padding} overflow-y-auto`}
            style={{ maxHeight }}
          >
            {children}
          </div>

          {/* Footer (if shown) */}
          {showFooter && (
            <div className="border-t border-gray-200 px-6 py-4">
              {footerContent ? (
                footerContent
              ) : (
                <div className="flex items-center justify-end gap-3">
                  <button
                    onClick={handleCancel}
                    disabled={isLoading}
                    className={`
                      px-4 py-2 text-sm font-medium rounded-lg transition-colors
                      ${isLoading 
                        ? 'opacity-50 cursor-not-allowed' 
                        : 'text-gray-700 hover:bg-gray-100'
                      }
                    `}
                  >
                    {cancelText}
                  </button>
                  
                  <button
                    onClick={handleConfirm}
                    disabled={isLoading || isConfirmDisabled}
                    className={`
                      px-4 py-2 text-sm font-medium rounded-lg transition-colors
                      ${confirmButtonClasses[confirmButtonType]}
                      ${(isLoading || isConfirmDisabled) 
                        ? 'opacity-50 cursor-not-allowed' 
                        : ''
                      }
                    `}
                  >
                    {isLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                        Loading...
                      </span>
                    ) : (
                      confirmText
                    )}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add CSS animation */}
      <style jsx>{`
        @keyframes modalSlideIn {
          from {
            opacity: 0;
            transform: translateY(-20px) scale(0.95);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
      `}</style>
    </div>
  );
};

// Modal Content Components for better composition
const ModalHeader = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-b border-gray-200 ${className}`}>
    {children}
  </div>
);

const ModalBody = ({ children, className = '', padding = true }) => (
  <div className={`${padding ? 'p-6' : ''} ${className}`}>
    {children}
  </div>
);

const ModalFooter = ({ children, className = '' }) => (
  <div className={`px-6 py-4 border-t border-gray-200 ${className}`}>
    {children}
  </div>
);

// Confirmation Modal Component
const ConfirmationModal = ({
  isOpen,
  onClose,
  title = 'Confirm Action',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  isLoading = false,
  type = 'warning', // warning, danger, info, success
  showCancel = true,
  showConfirm = true,
}) => {
  const iconMap = {
    warning: '⚠️',
    danger: '🔥',
    info: 'ℹ️',
    success: '✅',
  };

  const confirmButtonType = {
    warning: 'primary',
    danger: 'danger',
    info: 'primary',
    success: 'success',
  }[type];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      showCloseButton={!isLoading}
      showFooter
      confirmText={confirmText}
      cancelText={cancelText}
      onConfirm={onConfirm}
      onCancel={onCancel}
      isLoading={isLoading}
      confirmButtonType={confirmButtonType}
    >
      <div className="text-center">
        <div className="text-4xl mb-4">{iconMap[type]}</div>
        <p className="text-gray-700 whitespace-pre-line">{message}</p>
      </div>
    </Modal>
  );
};

// Alert Modal Component
// Alert Modal Component
const AlertModal = ({
  isOpen,
  onClose,
  title = 'Alert',
  message,
  buttonText = 'OK',
  type = 'info', // info, success, warning, error
  onButtonClick,
  showCloseButton = true, // 🆕 Add this prop
}) => {
  const iconMap = {
    info: 'ℹ️',
    success: '✅',
    warning: '⚠️',
    error: '❌',
  };

  const buttonClass = {
    info: 'bg-blue-600 hover:bg-blue-700',
    success: 'bg-green-600 hover:bg-green-700',
    warning: 'bg-orange-600 hover:bg-orange-700',
    error: 'bg-red-600 hover:bg-red-700',
  }[type];

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      showCloseButton={showCloseButton} // 🆕 Use the prop
      showFooter
      footerContent={
        <div className="text-center">
          <button
            onClick={onButtonClick || onClose}
            className={`px-6 py-2 text-white font-medium rounded-lg transition-colors ${buttonClass}`}
          >
            {buttonText}
          </button>
        </div>
      }
    >
      <div className="text-center">
        <div className="text-4xl mb-4">{iconMap[type]}</div>
        <p className="text-gray-700 whitespace-pre-line">{message}</p>
      </div>
    </Modal>
  );
};

// Loading Modal Component
const LoadingModal = ({
  isOpen,
  title = 'Loading',
  message = 'Please wait...',
  showSpinner = true,
  onClose = () => {},
}) => (
  <Modal
    isOpen={isOpen}
    onClose={onClose}
    title={title}
    size="sm"
    showCloseButton={false}
    showFooter={false}
    closeOnOverlayClick={false}
  >
    <div className="text-center py-8">
      {showSpinner && (
        <div className="mb-4">
          <div className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-orange-600 border-t-transparent" />
        </div>
      )}
      <p className="text-gray-600">{message}</p>
    </div>
  </Modal>
);

// Export everything
export default Modal;
export {
  ModalHeader,
  ModalBody,
  ModalFooter,
  ConfirmationModal,
  AlertModal,
  LoadingModal,
};